import { useEffect, useRef } from 'react';

type AppBackWindow = Window & {
  __backButtonInterceptor?: () => boolean;
};

/**
 * Makes the app-shell back button dismiss the topmost transient surface.
 * Interceptors stack: closing a child modal restores the parent's handler.
 */
export function useAppBackButtonInterceptor(isActive: boolean, onBack: () => void) {
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    if (!isActive) return;

    const appWindow = window as AppBackWindow;
    const previousInterceptor = appWindow.__backButtonInterceptor;
    const interceptor = () => {
      onBackRef.current();
      return true;
    };

    appWindow.__backButtonInterceptor = interceptor;
    return () => {
      if (appWindow.__backButtonInterceptor !== interceptor) return;
      if (previousInterceptor) appWindow.__backButtonInterceptor = previousInterceptor;
      else delete appWindow.__backButtonInterceptor;
    };
  }, [isActive]);
}
